// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as failoverDomainActions from 'common/modules/failoverDomain/actions';
import { combineReducers } from 'redux';
import {
    ActionType,
    getType,
} from 'typesafe-actions';
import {
    IPaginateApiResponse,
    paginateInitialState,
} from 'common/api/resources/Response';
import {
    IFailoverDomainDetailResponse,
    IFailoverDomainResponse,
} from 'common/api/resources/FailoverDomain';
import { IComputeResourceResponse } from 'common/api/resources/ComputeResource/model';

export type FailoverDomainAction = ActionType<typeof failoverDomainActions>;

interface IFailoverDomainState {
    list: IPaginateApiResponse<IFailoverDomainResponse[]>;
    item: IFailoverDomainDetailResponse;
    computeResources: IPaginateApiResponse<IComputeResourceResponse[]>;
}

export type FailoverDomainState = IFailoverDomainState;

const initialItem: IFailoverDomainDetailResponse = {
    id: 0,
    name: '',
    description: null,
    storage: { id: 0, name: '', free_space: 0 },
    total_compute_resources_count: 0,
    location: { id: 0, name: '', icon: null },
    ip_addresses: { used: 0, total: 0 },
    is_active: false,
    compute_resource_ids: [],
    recent_activities_count: 0,
    network: {
        ipv4_usage: { total: 0, used: 0 },
        ipv6_usage: { total: 0, used: 0 },
    },
    ip_blocks: [],
};

const updateListItem = (
    state: IPaginateApiResponse<IFailoverDomainResponse[]>,
    id: number,
    patch: Partial<IFailoverDomainResponse>
): IPaginateApiResponse<IFailoverDomainResponse[]> => ({
    ...state,
    data: state.data.map(item => item.id === id ? { ...item, ...patch } : item),
});

export default combineReducers<FailoverDomainState, FailoverDomainAction>({
    list: (state = paginateInitialState, action: FailoverDomainAction) => {
        switch (action.type) {
        case getType(failoverDomainActions.setFailoverDomainList):
            return action.payload;
        case getType(failoverDomainActions.updateFailoverDomainItem):
            return updateListItem(state, action.payload.id, action.payload);
        case getType(failoverDomainActions.setFailoverDomainItemIsLoading):
            return updateListItem(state, action.payload, { is_loading: true });
        case getType(failoverDomainActions.unsetFailoverDomainItemIsLoading):
            return updateListItem(state, action.payload, { is_loading: false });
        default:
            return state;
        }
    },
    item: (state = initialItem, action: FailoverDomainAction) => {
        switch (action.type) {
        case getType(failoverDomainActions.setFailoverDomainItem):
            return action.payload;
        case getType(failoverDomainActions.updateFailoverDomainItem): {
            if (state.id !== action.payload.id) {
                return state;
            }
            const { storage: _storage, ...rest } = action.payload;
            return { ...state, ...rest };
        }
        case getType(failoverDomainActions.setFailoverDomainItemIsLoading):
            return state.id === action.payload ? { ...state, is_loading: true } : state;
        case getType(failoverDomainActions.unsetFailoverDomainItemIsLoading):
            return state.id === action.payload ? { ...state, is_loading: false } : state;
        default:
            return state;
        }
    },
    computeResources: (state = paginateInitialState, action: FailoverDomainAction) => {
        switch (action.type) {
        case getType(failoverDomainActions.setFailoverDomainComputeResources):
            return action.payload;
        case getType(failoverDomainActions.appendFailoverDomainComputeResources):
            return {
                ...action.payload,
                data: [...state.data, ...action.payload.data],
            };
        default:
            return state;
        }
    },
});
